import { CHANNELS } from "../../shared/channels";
import { call, isWeb, listen, mediaSrc } from "../bridge";
import { byId, flashToast, renderList } from "../dom";
import { t } from "../i18n";
import { onViewShown } from "../navigation";
import { formatBytes, formatDate, shrinkImage } from "../utils";

// one scan library everywhere. the desk scans on the brother and uploads straight away,
// phones add photos of sketches. anything stuck in the pc inbox shows up for a one tap upload

interface ScanEntry {
  id: string;
  name: string;
  fileName: string;
  fileUrl: string;
  bytes: number;
  scannedAt: string;
  source: "inbox" | "library";
}

let inbox: ScanEntry[] = [];
let library: ScanEntry[] = [];
let open: ScanEntry | null = null;

const isPdf = (entry: ScanEntry) => /\.pdf$/i.test(entry.fileName);

function status(text: string): void {
  byId("scan-status").hidden = !text;
  byId("scan-status-text").textContent = text;
}

function tile(entry: ScanEntry): HTMLElement {
  const card = document.createElement("button");
  card.type = "button";
  card.className = `tile scan-tile${entry.source === "inbox" ? " pending" : ""}`;
  const media = document.createElement("span");
  media.className = "tile-media";
  if (isPdf(entry)) media.textContent = "PDF";
  else {
    const image = document.createElement("img");
    image.alt = "";
    image.loading = "lazy";
    image.decoding = "async";
    image.src = entry.source === "library" ? mediaSrc(entry.fileUrl) : entry.fileUrl;
    media.append(image);
  }
  const title = document.createElement("span");
  title.className = "tile-title";
  title.textContent = formatDate(entry.scannedAt) || entry.name;
  const meta = document.createElement("span");
  meta.className = "tile-meta";
  meta.textContent = entry.source === "inbox" ? t("tap to upload") : formatBytes(entry.bytes);
  card.append(media, title, meta);
  card.addEventListener("click", () => (entry.source === "inbox" ? void upload([entry]) : openViewer(entry)));
  return card;
}

function paint(): void {
  const pending = inbox.filter((entry) => !library.some((kept) => kept.fileName === entry.fileName));
  byId("scan-pc").hidden = isWeb() || pending.length === 0;
  renderList(byId("scan-inbox"), pending, tile);
  renderList(byId("scan-library"), library, tile);
  byId("scan-library-empty").hidden = library.length > 0;
  byId("scan-count").textContent = library.length ? t("{count} kept", { count: library.length }) : "";
}

async function reload(): Promise<void> {
  if (!isWeb()) {
    try {
      inbox = await call<ScanEntry[]>(CHANNELS.scansInbox);
    } catch {
      inbox = [];
    }
  }
  try {
    library = (await call<{ library: ScanEntry[] }>(CHANNELS.scansList)).library || [];
  } catch {
    library = [];
  }
  paint();
}

async function upload(entries: ScanEntry[]): Promise<void> {
  try {
    for (const [index, entry] of entries.entries()) {
      status(t("uploading {n} of {total}", { n: index + 1, total: entries.length }));
      const dataUrl = await call<string>(CHANNELS.scansRead, entry.fileName);
      await call(CHANNELS.scansSave, entry.fileName, dataUrl);
    }
    flashToast(entries.length === 1 ? t("scan kept") : t("{count} scans kept", { count: entries.length }));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("upload failed"));
  } finally {
    status("");
    await reload();
  }
}

async function capture(): Promise<void> {
  const button = byId<HTMLButtonElement>("scan-capture");
  button.disabled = true;
  status(t("the brother is scanning"));
  try {
    const entry = await call<ScanEntry>(CHANNELS.scansCapture);
    await upload([entry]);
    await reload();
    const kept = library.find((item) => item.fileName === entry.fileName);
    if (kept) openViewer(kept);
  } catch (error) {
    status("");
    flashToast(error instanceof Error ? error.message : t("scan failed"));
  } finally {
    button.disabled = false;
  }
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(t("could not read that file")));
    reader.readAsDataURL(file);
  });
}

async function addPhotos(files: File[]): Promise<void> {
  if (!files.length) return;
  try {
    for (const [index, file] of files.entries()) {
      status(t("saving {n} of {total}", { n: index + 1, total: files.length }));
      const pdf = file.type === "application/pdf";
      const dataUrl = pdf ? await fileToDataUrl(file) : await shrinkImage(file, 2400);
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      await call(CHANNELS.scansSave, `Photo_${stamp}_${index + 1}.${pdf ? "pdf" : "jpg"}`, dataUrl);
    }
    flashToast(t("added to scans"));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not save that"));
  } finally {
    status("");
    await reload();
  }
}

function openViewer(entry: ScanEntry): void {
  open = entry;
  const url = mediaSrc(entry.fileUrl);
  const image = byId<HTMLImageElement>("scan-viewer-image");
  const pdf = byId("scan-viewer-pdf");
  image.hidden = isPdf(entry);
  pdf.hidden = !isPdf(entry);
  if (isPdf(entry)) {
    image.removeAttribute("src");
    pdf.textContent = t("pdf scan, use download to open it");
  } else image.src = url;
  byId("scan-viewer-name").textContent = `${entry.name} · ${formatBytes(entry.bytes)}`;
  const download = byId<HTMLAnchorElement>("scan-viewer-download");
  download.href = url;
  download.download = entry.fileName;
  byId("scan-viewer").hidden = false;
}

function closeViewer(): void {
  open = null;
  byId("scan-viewer").hidden = true;
}

export function setupScanView(): void {
  byId("scan-capture").addEventListener("click", () => void capture());
  byId("scan-upload-all").addEventListener("click", () => {
    const pending = inbox.filter((entry) => !library.some((kept) => kept.fileName === entry.fileName));
    if (pending.length) void upload(pending);
  });
  const photo = byId<HTMLInputElement>("scan-photo");
  photo.addEventListener("change", async () => {
    await addPhotos([...(photo.files || [])]);
    photo.value = "";
  });
  byId("scan-viewer-close").addEventListener("click", closeViewer);
  byId("scan-viewer-download").addEventListener("click", (event) => {
    if (isWeb()) return;
    event.preventDefault();
    void call(CHANNELS.openExternal, byId<HTMLAnchorElement>("scan-viewer-download").href);
  });
  byId("scan-viewer-delete").addEventListener("click", async () => {
    if (!open || !confirm(t("delete this scan?"))) return;
    await call(CHANNELS.scansRemove, open.fileName);
    closeViewer();
    await reload();
  });
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && open) closeViewer();
  });
  listen(CHANNELS.scansUpdated, () => {
    if (!byId("view-scan").classList.contains("active")) return;
    void reload();
  });
  onViewShown("scan", () => void reload());
}
